import { MessageCircle, Phone, Instagram, X } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export default function WhatsAppFloat() {
  const [open, setOpen] = useState(false);
  const [peeked, setPeeked] = useState(false);

  // First-time peek tooltip — teaser after 6s if user hasn't interacted
  useEffect(() => {
    const dismissed = localStorage.getItem("iw_reach_peeked");
    if (dismissed) return;
    const t = setTimeout(() => setPeeked(true), 6000);
    const hide = setTimeout(() => setPeeked(false), 13000);
    return () => {
      clearTimeout(t);
      clearTimeout(hide);
    };
  }, []);

  const dismissPeek = () => {
    setPeeked(false);
    localStorage.setItem("iw_reach_peeked", "1");
  };

  const toggle = () => {
    dismissPeek();
    setOpen((v) => !v);
  };

  return (
    <div
      className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 flex flex-col items-end gap-3"
      data-testid="whatsapp-float"
    >
      {/* Peek teaser */}
      <AnimatePresence>
        {peeked && !open && (
          <motion.div
            initial={{ opacity: 0, x: 12, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 8, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative mb-1 max-w-[260px]"
          >
            <div
              className="relative px-4 py-3 pr-8 text-right"
              style={{
                background:
                  "linear-gradient(135deg, rgba(26,20,20,0.95) 0%, rgba(42,26,30,0.95) 100%)",
                border: "1px solid rgba(212,175,55,0.55)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 10px 40px -10px rgba(232,165,180,0.4)",
              }}
            >
              <span
                aria-hidden="true"
                className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-3 h-3 rotate-45"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(26,20,20,0.95) 0%, rgba(42,26,30,0.95) 100%)",
                  borderRight: "1px solid rgba(212,175,55,0.55)",
                  borderTop: "1px solid rgba(212,175,55,0.55)",
                }}
              />
              <span
                className="italic text-[var(--iw-ivory)] block"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                A real person is{" "}
                <span className="gold-rose-gradient not-italic font-medium">
                  1 tap away
                </span>
              </span>
              <button
                onClick={dismissPeek}
                aria-label="Dismiss"
                className="absolute top-1 right-1 w-5 h-5 grid place-items-center text-[var(--iw-faint)] hover:text-[var(--iw-gold)] text-xs"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-2.5 glass-card px-5 py-4 min-w-[240px]"
            data-testid="reach-us-panel"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="overline text-[var(--iw-gold)]">Reach Us</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="w-5 h-5 grid place-items-center text-[var(--iw-faint)] hover:text-[var(--iw-gold)]"
              >
                <X size={13} />
              </button>
            </div>
            <a
              href={`https://wa.me/${BRAND.whatsapp}?text=Hi%20Invitation%20Wala%2C%20I%27d%20love%20to%20discuss%20a%20project.`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 text-[var(--iw-ivory)] hover:text-[var(--iw-gold)] transition text-sm"
              data-testid="whatsapp-float-chat"
            >
              <MessageCircle size={16} className="text-[var(--iw-gold)]" />
              WhatsApp chat
            </a>
            <a
              href={`tel:${BRAND.whatsapp}`}
              className="flex items-center gap-3 text-[var(--iw-ivory)] hover:text-[var(--iw-gold)] transition text-sm"
              data-testid="whatsapp-float-call"
            >
              <Phone size={16} className="text-[var(--iw-gold)]" />
              Call {BRAND.whatsappDisplay}
            </a>
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 text-[var(--iw-ivory)] hover:text-[var(--iw-rose)] transition text-sm"
              data-testid="whatsapp-float-instagram"
            >
              <Instagram size={16} className="text-[var(--iw-rose)]" />
              @{BRAND.instagram.toLowerCase()}
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-3">
        {/* Instagram standalone button — one-tap to IG */}
        <a
          href={BRAND.instagramUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Follow on Instagram"
          data-testid="instagram-float-btn"
          className="group relative w-12 h-12 rounded-full grid place-items-center transition-transform duration-300 hover:scale-110"
          style={{
            background:
              "linear-gradient(135deg, #E8A5B4 0%, #D4AF37 50%, #b87a70 100%)",
            color: "#0a0a0a",
            boxShadow: "0 10px 30px -10px rgba(232,165,180,0.7)",
          }}
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              border: "1px solid rgba(232,165,180,0.45)",
              animationDuration: "3s",
            }}
          />
          <Instagram size={18} strokeWidth={2} />
        </a>

        <button
          onClick={toggle}
          className="w-14 h-14 rounded-full grid place-items-center transition-transform duration-300 hover:scale-110 relative"
          style={{
            background:
              "linear-gradient(135deg, var(--iw-gold) 0%, var(--iw-rose-deep) 100%)",
            color: "#0a0a0a",
            boxShadow: "0 10px 40px -10px rgba(232,165,180,0.7)",
          }}
          aria-label="Get your digital invite now"
          data-testid="whatsapp-float-toggle"
        >
          {!open && (
            <>
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full animate-ping"
                style={{
                  border: "1px solid rgba(212,175,55,0.5)",
                  animationDuration: "2.8s",
                }}
              />
              <span
                aria-hidden="true"
                className="hidden md:block absolute right-[calc(100%+12px)] top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 text-[12px] tracking-wide pointer-events-none"
                style={{
                  background: "rgba(10,10,10,0.85)",
                  border: "1px solid rgba(212,175,55,0.5)",
                  color: "var(--iw-ivory)",
                  fontFamily: "Outfit, sans-serif",
                  backdropFilter: "blur(8px)",
                }}
              >
                Get Your Digital Invite Now!
              </span>
            </>
          )}
          {open ? <X size={22} strokeWidth={2} /> : <MessageCircle size={22} strokeWidth={2} />}
        </button>
      </div>
    </div>
  );
}
